<template>
    <section class="discussion-work-selector">
        <label for="discussion-work-search" class="block text-sm font-semibold text-foxy-text">
            Пов’язати дискусію з книгою <span class="foxy-muted font-normal">(необов’язково)</span>
        </label>
        <p v-if="locked" class="foxy-muted mt-2 text-sm">
            Дискусія пов’язана з кількома книгами. Ці зв’язки буде збережено без змін.
        </p>
        <template v-else>
            <input type="hidden" name="work_id" :value="selected ? selected.id : ''" :disabled="!explicit">
            <div v-if="selected" class="mt-3 flex min-w-0 items-center gap-3">
                <img v-if="selected.cover" :src="selected.cover" alt="" class="h-14 w-10 shrink-0 rounded object-cover">
                <div class="min-w-0 flex-1">
                    <p class="break-words font-medium text-foxy-text">{{ selected.title }}</p>
                    <p class="foxy-muted break-words text-sm">{{ selected.author }}</p>
                </div>
                <div class="flex shrink-0 flex-col gap-2">
                    <button ref="change" type="button" class="min-h-[44px] px-2 text-sm text-foxy-action" @click="change">Змінити</button>
                    <button type="button" class="min-h-[44px] px-2 text-sm text-foxy-action" @click="remove">Прибрати</button>
                </div>
            </div>
            <div v-if="searching" class="mt-3">
                <input id="discussion-work-search" ref="search" v-model="query" type="search"
                    class="foxy-input w-full" autocomplete="off" placeholder="Назва книги або автор"
                    aria-describedby="discussion-work-help" aria-controls="discussion-work-results"
                    :aria-expanded="results.length > 0 ? 'true' : 'false'"
                    @input="queueSearch" @keydown.down.prevent="focusResult(0)" @keydown.esc.prevent="dismiss">
                <p id="discussion-work-help" class="foxy-muted mt-1 text-xs">Введіть щонайменше 2 символи. Вибір збережеться разом із дискусією.</p>
                <p role="status" aria-live="polite" class="foxy-muted mt-2 text-sm">{{ message }}</p>
                <ul v-if="results.length" id="discussion-work-results" class="work-results mt-2 rounded-lg border border-foxy-border">
                    <li v-for="(book, index) in results" :key="book.id">
                        <button ref="results" type="button" class="work-result"
                            @click="select(book)" @keydown.down.prevent="focusResult(index + 1)"
                            @keydown.up.prevent="focusResult(index - 1)" @keydown.esc.prevent="dismiss">
                            <img v-if="book.cover" :src="book.cover" alt="" class="h-12 w-9 shrink-0 rounded object-cover">
                            <span class="min-w-0">
                                <span class="block break-words font-medium">{{ book.title }}</span>
                                <span class="foxy-muted block break-words text-sm">{{ book.author }}</span>
                            </span>
                        </button>
                    </li>
                </ul>
            </div>
        </template>
    </section>
</template>

<script>
import axios from 'axios';

export default {
    name: 'DiscussionWorkSelector',
    props: {
        initialSelection: { type: Object, default: null },
        initiallyExplicit: { type: Boolean, default: false },
        locked: { type: Boolean, default: false },
    },
    data() {
        return {
            selected: this.initialSelection,
            explicit: this.initiallyExplicit,
            searching: !this.initialSelection,
            query: '',
            results: [],
            message: '',
            requestId: 0,
            timer: null,
        };
    },
    beforeDestroy() {
        this.invalidate();
    },
    methods: {
        invalidate() {
            clearTimeout(this.timer);
            this.requestId += 1;
            this.results = [];
            this.message = '';
        },
        queueSearch() {
            this.invalidate();
            const query = this.query.trim();
            if (query.length < 2 || this.locked) return;
            const requestId = this.requestId;
            this.timer = setTimeout(() => this.search(query, requestId), 350);
        },
        async search(query, requestId) {
            if (requestId !== this.requestId || query !== this.query.trim() || this.locked) return;
            this.message = 'Шукаємо…';
            try {
                const { data } = await axios.get('/api/book-selection', { params: { q: query } });
                if (requestId !== this.requestId || query !== this.query.trim()) return;
                this.results = (data.books || []).slice(0, 12);
                this.message = this.results.length ? '' : 'Книг не знайдено.';
            } catch (error) {
                if (requestId === this.requestId) this.message = 'Не вдалося завантажити книги. Спробуйте ще раз.';
            }
        },
        select(book) {
            if (this.locked) return;
            this.invalidate();
            this.selected = book;
            this.explicit = true;
            this.searching = false;
            this.query = '';
            this.$nextTick(() => this.$refs.change?.focus());
        },
        change() {
            if (this.locked) return;
            this.invalidate();
            this.searching = true;
            this.query = '';
            this.$nextTick(() => this.$refs.search.focus());
        },
        remove() {
            if (this.locked) return;
            this.invalidate();
            this.selected = null;
            this.explicit = true;
            this.searching = true;
            this.query = '';
            this.$nextTick(() => this.$refs.search.focus());
        },
        dismiss() {
            this.invalidate();
            this.$refs.search.focus();
        },
        focusResult(index) {
            const buttons = this.$refs.results || [];
            if (index < 0 || !buttons.length) this.$refs.search.focus();
            else buttons[Math.min(index, buttons.length - 1)].focus();
        },
    },
};
</script>

<style scoped>
.discussion-work-selector { width: 100%; min-width: 0; }
.work-results { max-height: 20rem; overflow-y: auto; overflow-x: hidden; }
.work-result { display: flex; align-items: center; gap: .75rem; width: 100%; padding: .75rem; text-align: left; color: rgb(var(--foxy-text)); }
.work-result:hover, .work-result:focus-visible { background: rgb(var(--foxy-elevated)); outline: 2px solid rgb(var(--foxy-action)); outline-offset: -2px; }
</style>
